import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { elementAction, feedbackAction } from "../../store";
import { Trans, useTranslation } from "react-i18next";
import { Keyboard } from "../control";
import { Button, Checkbox } from "../elements";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

export default function PrivacyNotificationDialog(props) {
  const { id, t } = props;
  const { t: privacyTrans, ready } = useTranslation("md:privacy");

  const ppSchema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      a: [
        ...(defaultSchema.attributes?.a || []),
        "target", // 允許 target
        "rel", // 建議一起允許 rel
      ],
    },
  };
  const md = privacyTrans("__markdown__");
  const dispatch = useDispatch();
  const policyNoticeContentRef = useRef();
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const theme = useSelector((state) => state.element.theme);
  const isShowPrivacyNotificationDialog = useSelector(
    (state) => state.element.isShowPrivacyNotificationDialog
  );
  const isPrivacyNotificationContentFocused = useSelector(
    (state) => state.element.isPrivacyNotificationContentFocused
  );
  const privacyPolicyAbortAge = useSelector(
    (state) => state.feedback.privacyPolicyAbortAge
  );
  const geoCountry = useSelector((state) => state.feedback.geoCountry);
  const { setAgreePrivacyPolicy } = feedbackAction;

  const [focusId, setFocusId] = useState("not_agree");
  const [selected, setSelected] = useState([0, 0]);
  const [showBottomAgreeButton, setShowBottomAgreeButton] = useState(true);
  const [showPrivacyPolicyAbortAge, setShowPrivacyPolicyAbortAge] =
    useState(false);
  const [isAgreePrivacy, setIsAgreePrivacy] = useState(false);

  const dispatchDisableKeyboard = (disabled) => {
    dispatch(elementAction.setEnableRootKeyboardEvent(!disabled));
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isShowPrivacyNotificationDialog) {
      dialog.showModal();
      dispatchDisableKeyboard(true);
      // 初始將焦點放在內容
      requestAnimationFrame(() => {
        policyNoticeContentRef.current?.focus();
        dispatch(elementAction.setPrivacyNotificationContentFocused(true));
      });
    } else {
      dialog.close();
      dispatchDisableKeyboard(false);
    }
  }, [isShowPrivacyNotificationDialog]);

  useEffect(() => {
    if (privacyPolicyAbortAge > 0) {
      setShowPrivacyPolicyAbortAge(true);
      setSelected([0, 0]);
    } else {
      setShowPrivacyPolicyAbortAge(false);
      setSelected([0, 0]);
    }

  }, [privacyPolicyAbortAge]);

  useEffect(() => {
    if (showBottomAgreeButton) {
      if (privacyPolicyAbortAge === 0) {
        setFocusId("not_agree");
        setIsAgreePrivacy(true);
      } else {
        setFocusId("age");
        setIsAgreePrivacy(false);
      }
    } else {
      setFocusId("content");
      setIsAgreePrivacy(false);
    }
  }, [showBottomAgreeButton, privacyPolicyAbortAge]);

  useEffect(() => {
    if (!isAgreePrivacy) {
      dispatch(setAgreePrivacyPolicy(false));
    }
  }, [isAgreePrivacy]);

  const agreePrivacy = () => {
    dispatch(feedbackAction.setPrivacyNoticeAgreedAt());
    dispatch(feedbackAction.setAgreePrivacyPolicy(true));
    dispatch(feedbackAction.checkSubmit());
    dispatch(elementAction.triggerPrivacyNotificationDialog(false));
  };

  const disagree = () => {
    dispatch(feedbackAction.setAgreePrivacyPolicy(false));
    dispatch(elementAction.triggerDialog({ id, isOpen: false }));
  };

  const clickEnablePrivacy = () => {
    if (showBottomAgreeButton) {
      setIsAgreePrivacy(!isAgreePrivacy);
      setFocusId("not_agree");
    }
  };

  const scrollContent = (direct, replace = false) => {
    if (policyNoticeContentRef.current) {
      if (replace) {
        policyNoticeContentRef.current.scrollTop = direct;
      } else {
        policyNoticeContentRef.current.scrollTop += direct;
      }
    }
  };

  const onScroll = () => {
    if (policyNoticeContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        policyNoticeContentRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5;
      setShowBottomAgreeButton(isNearBottom);
    }
  };

  useEffect(() => {
    const el = policyNoticeContentRef.current;
    if (el) {
      el.addEventListener("scroll", onScroll);
      scrollContent(0, true);
      return () => {
        el.removeEventListener("scroll", onScroll);
      };
    }
  }, [policyNoticeContentRef]);

  const buttonsTabLocked = !showBottomAgreeButton;

  const options = useMemo(() => {
    const opts = [];
    if (showPrivacyPolicyAbortAge) {
      opts.push(["privacy_notice_above_age"]);
    }
    opts.push(["not_agree"]);
    opts.push(["agree"]);
    return opts;
  }, [showPrivacyPolicyAbortAge]);

  const keyEnter = () => {
    const current = options[selected[0]]?.[selected[1]];
    switch (current) {
      case "privacy_notice_above_age":
        clickEnablePrivacy();
        break;
      case "not_agree":
        disagree();
        break;
      case "agree":
        agreePrivacy();
        break;
    }
  };

  const keyEsc = (event) => {
    event.preventDefault();
    if (isShowPrivacyNotificationDialog) {
      dispatch(elementAction.setEnableRootKeyboardEvent(true));
      dispatch(elementAction.triggerPrivacyNotificationDialog(false));
      dispatch(elementAction.setPrivacyNotificationContentFocused(false));
    }
  };

  const handleDialogKeyDown = (e) => {
    if (isPrivacyNotificationContentFocused) {
      if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        const { scrollTop, scrollHeight, clientHeight } =
          policyNoticeContentRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;

        if (isAtBottom) {
          dispatch(elementAction.setPrivacyNotificationContentFocused(false));
        } else {
          scrollContent(50); // Scroll down
        }
      } else if (e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey)) {
        e.preventDefault();
        scrollContent(-50); // Scroll up
      } else if (e.key === "Escape") {
        e.preventDefault();
        dispatch(elementAction.setEnableRootKeyboardEvent(true));
        dispatch(elementAction.triggerPrivacyNotificationDialog(false));
        dispatch(elementAction.setPrivacyNotificationContentFocused(false));
      }
    }
  };

  return (
    <dialog
      ref={dialogRef}
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      className="bg-transparent font-sans antialiased"
      onKeyDown={handleDialogKeyDown}
    >
      <Keyboard
        isEnabled={
          isShowPrivacyNotificationDialog &&
          !isPrivacyNotificationContentFocused
        }
        options={options}
        selected={selected}
        setSelected={setSelected}
        keyEnter={keyEnter}
        keyEsc={keyEsc}
      />
      <div
        id={`${id}-overlay`}
        className={`fixed inset-0 bg-${theme} bg-opacity-80`}
      />
      <div className="w-full h-full bg-transparent relative flex justify-center items-center">
        <div
          className={`relative rounded-md w-[90vw] md:w-[70vw] lg:w-[960px] xl:w-[960px] h-[600px] bg-${theme} flex flex-col border-[1px] border-${theme}-checkbox-disabled `}
        >
          <div className={`w-full`}>
            <div className={`px-6 text-${theme}-normal`}>
              <h1
                id={`${id}-title`}
                ref={headingRef}
                role="heading"
                aria-level={1}
                tabIndex={-1}
                className="text-${theme}-normal py-2"
              >
                {t("privacyNotice")}
              </h1>
              {/*Privacy Notice Start */}
              <div className={"relative"}>
                <div
                  ref={policyNoticeContentRef}
                  id={"policyNoticeContent"}
                  role="region"
                  aria-labelledby={`${id}-title`}
                  tabIndex={0}
                  className={`pt-1 text-${theme}-normal text-left mt-3 font-light ${showPrivacyPolicyAbortAge ? 'h-[380px]': 'h-[430px]'} w-full scrollbar overflow-y-scroll space-y-5 z-998 px-8 ${
                    theme === "rog" ? "prose-invert" : ""
                  }`}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeRaw], [rehypeSanitize, ppSchema]]}
                    components={{
                      li: ({ node, ...props }) => (
                        <li
                          className="list-decimal ml-6 whitespace-pre-line"
                          {...props}
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          className="text-remote_code_link underline break-all"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {ready && md}
                  </ReactMarkdown>
                  {/* <Trans i18nKey="privacyNoticeContent">
                    <a
                      className="underline"
                      href={t("privacy_notice_link")}
                      target={"new"}
                    />
                  </Trans> */}
                </div>
                <div
                  hidden={showBottomAgreeButton}
                  className={`pointer-events-none	absolute bottom-0 left-0 w-full h-14 bg-gradient-to-t from-${theme} to-transparent`}
                ></div>
              </div>
              {/*Privacy Notice End */}
              {showPrivacyPolicyAbortAge && (
                <div className="flex justify-left items-center w-full mt-5 ">
                  <Checkbox
                    id={"privacy_notice_above_age"}
                    label={t("privacy_notice_above_age_text", {
                      above_age: privacyPolicyAbortAge,
                    })}
                    className={"flex max-w-full items-center"}
                    theme={theme}
                    isFocus={
                      options[selected[0]]?.[selected[1]] ===
                      "privacy_notice_above_age"
                    }
                    isEnabled={showBottomAgreeButton}
                    isChecked={isAgreePrivacy}
                    onClick={clickEnablePrivacy}
                  />
                </div>
              )}
              <div className="flex flex-row w-full mt-12 space-x-5 md:space-y-0 justify-between items-center mb-4 px-8">
                <Button
                  role="button"
                  id={"not_agree"}
                  isFocus={(name) =>
                    name === options[selected[0]]?.[selected[1]]
                  }
                  theme={theme}
                  onClick={disagree}
                  tabIndex={0}
                  className={`h-[32px] w-[132px] text-2xl px-4 border font-semibold relative group-hover:border-4 group-hover:border-btn-hover-light group-active:bg-btn-click group-active:border-btn-click`}
                >
                  {t("not_agree")}
                </Button>
                <Button
                  role="button"
                  id={"agree"}
                  isFocus={(name) =>
                    name === options[selected[0]]?.[selected[1]]
                  }
                  theme={theme}
                  onClick={agreePrivacy}
                  isEnabled={!buttonsTabLocked && isAgreePrivacy}
                  aria-disabled={buttonsTabLocked || !isAgreePrivacy}
                  tabIndex={buttonsTabLocked ? -1 : 0}
                  className={`h-[32px] w-[132px] text-2xl px-4 border font-semibold relative group-hover:border-4 group-hover:border-btn-hover-light group-active:bg-btn-click group-active:border-btn-click`}
                >
                  {t("agree")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
}
